import { ComponentType } from 'react';

export enum StepStatus {
  COMPLETED = 'completed',
  CURRENT = 'current',
  IN_PROGRESS = 'in_progress', // Step has been reached but not completed
  UPCOMING = 'upcoming',
}

export enum SubStepStatus {
  COMPLETED = 'completed',
  CURRENT = 'current',
  ACCESSIBLE = 'accessible',
  UPCOMING = 'upcoming',
}

export interface StepItem {
  /**
   * Unique identifier for the step
   */
  id: string;
  /**
   * Title text for the step
   */
  title: string;
  /**
   * Optional description text for the step
   */
  description?: string;
  /**
   * Current status of the step
   */
  status: StepStatus;
  /**
   * Whether this step is optional
   */
  optional?: boolean;
  /**
   * Custom component to render for this step
   */
  component?: ComponentType<{ step: StepItem }>;
  /**
   * Optional sub-steps for this step
   */
  subSteps?: SubStepItem[];
}

export interface SubStepItem {
  /**
   * Unique identifier for the sub-step
   */
  id: string;
  /**
   * Title text for the sub-step
   */
  title: string;
  /**
   * Current status of the sub-step
   */
  status: SubStepStatus;
}

export interface StepListProps {
  /**
   * Array of step items to display
   */
  steps: StepItem[];
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Whether to show step numbers
   * @default true
   */
  showNumbers?: boolean;
  /**
   * Callback when a step is clicked
   */
  onStepClick?: (stepId: string) => void;
  /**
   * Callback when a sub-step is clicked
   */
  onSubStepClick?: (stepId: string, subStepId: string) => void;
  /**
   * Set of expanded step ids
   */
  expandedStepIds?: Set<string>;
  /**
   * Callback when a step is toggled
   */
  onToggleStep?: (stepId: string) => void;
  /**
   * When true, all steps are clickable regardless of status (useful for dev/testing)
   * @default false
   */
  allStepsClickable?: boolean;
  /**
   * Custom component to render each step
   */
  component?: ComponentType<{
    step: StepItem;
    completedText?: string;
    editText?: string;
    resumeText?: string;
    startText?: string;
    showSubSubSteps?: boolean;
  }>;
  /**
   * Whether to show sub-sub steps
   * @default false
   */
  showSubSubSteps?: boolean;
  /**
   * Text for completed steps
   * @default 'Completed'
   */
  completedText?: string;
  /**
   * Text for edit steps
   * @default 'Edit'
   */
  editText?: string;
  /**
   * Text for resume steps
   * @default 'Resume'
   */
  resumeText?: string;
  /**
   * Text for start steps
   * @default 'Start'
   */
  startText?: string;
  /**
   * A set of step IDs that are allowed to show the caret and substeps even if they only have one substep
   */
  alwaysShowSubStepsForStepIds?: Set<string>;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}
